import { TabContent, Row, Column, FlexBox, Typography, RatingsStars, Ul, Li, Link, Box, Card, AlertBox, useScreenClass, Callout, FluidImage } from '@vp/swan'

import StockOptionCTA from './StockOptionCTA'
import { useAppContext } from '../../state/AppContext'
import { isOutOfStock, MpvIdsContent } from './StockOptionUtil'
import { CampaignCallout } from '@vp/campaign-callouts'
import { CouponCallout } from '@vp/coupon-callout'
import { stylesheet } from './StockOptionSection.scss'
import { PriceDisplay } from '@vp/price-platform'
import { BFFPageData } from '../../services/serviceClients/BffClient'
import { trackNavigationClicks } from '../../utils/tracking/tracking'
import { useStyles } from '@vp/ubik-context'
import { formatLocale } from '../../utils/buildHelpers'

export interface StockOptionContentProps {
  mpvId: string
  item: MpvIdsContent
  browseDesign: {
    link: string
  }
  browseDesignCta: string
  uploadDesignCta: string
  viewDetailsCta: string
  highlightsSectionTitle: string
  optionsSectionTitle: string
  tabIndex: number
  enableCampaignCallouts: boolean
  outOfStockContent: string
  pageDataFromBFF: BFFPageData
  isActiveTab?: boolean
  currentTabId: string
}

const StockOptionContent = (props: StockOptionContentProps) => {
  const {
    mpvId,
    item,
    browseDesignCta,
    uploadDesignCta,
    viewDetailsCta,
    browseDesign,
    highlightsSectionTitle,
    optionsSectionTitle,
    tabIndex,
    enableCampaignCallouts,
    outOfStockContent,
    pageDataFromBFF,
    currentTabId,
    isActiveTab,
  } = props
  useStyles(stylesheet)
  const { reviewsData, merchandisingCategoryId, locale } = useAppContext()
  const isMobile = useScreenClass() === 'xs'
  const siteUrl: Record<string, string> = {
    'en-US': 'vistaprint.com',
    'es-US': 'vistaprint.com',
    'en-GB': 'vistaprint.co.uk',
  }
  const destinationUrl = `https://${siteUrl[formatLocale(locale, true)]}${item.pdpLink}`

  return (
    isActiveTab && (
      <>
        <TabContent key={mpvId} tabId={currentTabId}>
          <Row>
            {isOutOfStock(mpvId, pageDataFromBFF) && (
              <Column span={12} marginBottom={6}>
                <AlertBox skin="warning" data-testid="outOfStockAlert">
                  {outOfStockContent}
                </AlertBox>
              </Column>
            )}
            <Column span={6} marginBottom={{ xs: 7, sm: 7, md: 8 }}>
              <Box>
                <Card fullBleed overflow="hidden" style={{ aspectRatio: 1.6 }}>
                  <FluidImage src={item.imageUrl} alt="" loading="lazy" data-testid="heroImage" />
                </Card>
              </Box>
            </Column>
            <Column span={6}>
              <Row>
                <Column span={12} marginBottom={6}>
                  <FlexBox flexDirection="row" justifyContent="space-between" alignItems="center" flexWrap={isMobile ? 'wrap' : 'unset'} gap={1}>
                    <Typography fontSkin="title-section" marginBottom={2} data-testid="title">
                      {item.merchandisingName}
                      {reviewsData?.[mpvId as string] && (
                        <RatingsStars
                          data-testid="ratingsStars"
                          style={{
                            display: !isMobile ? 'inline-block' : 'unset',
                          }}
                          ratingValue={reviewsData?.[mpvId]?.averageRating}
                          reviews={reviewsData?.[mpvId]?.reviewsCount}
                          fontSkin="body-standard"
                          marginLeft={3}
                        />
                      )}
                    </Typography>
                    <Link
                      data-testid="viewDetailsLink"
                      marginBottom={{ xs: 3 }}
                      href={destinationUrl}
                      fontSkin="body-standard"
                      covering
                      onClick={() => {
                        trackNavigationClicks({
                          categoryId: merchandisingCategoryId,
                          pageZone: 'stockOptions',
                          destinationUrl,
                          tabIndex: tabIndex + 1,
                          stockOptionsCtaText: 'View full details header',
                        })
                      }}
                    />
                  </FlexBox>
                  {enableCampaignCallouts && !isOutOfStock(mpvId, pageDataFromBFF) && (
                    <CampaignCallout data-testid="campaignCallout" product={mpvId} marginBottom={{ xs: 3 }} marginRight={3} />
                  )}
                  {item.callout && (
                    <Callout skin={item.callout.skin} marginBottom={{ xs: 3 }} data-testid="callout">
                      {item.callout.text}
                    </Callout>
                  )}
                  <Typography fontSkin="body-standard" data-testid="marketingText">
                    {item.marketingText}
                  </Typography>
                </Column>
              </Row>
              <Row>
                <Column span={6} spanXs={6}>
                  <Typography fontSkin="title-subsection" marginBottom={3} data-testid="highlightsSectionTitle">
                    {highlightsSectionTitle}
                  </Typography>
                  <Ul>
                    {item.highlights.map((value: string) => {
                      return (
                        <Li key={value} fontSkin="body-standard" data-testid="highlightItem">
                          {value}
                        </Li>
                      )
                    })}
                  </Ul>
                </Column>
                <Column span={6} spanXs={6}>
                  <Typography fontSkin="title-subsection" marginBottom={3} data-testid="optionsSectionTitle">
                    {optionsSectionTitle}
                  </Typography>
                  <Ul>
                    {Object.entries(item.options).map(([optionType, optionValue]) => {
                      return (
                        <Li key={optionType} data-testid="optionItem">
                          <Typography fontSkin="body-standard-bold" as="span">
                            {optionType}:
                          </Typography>{' '}
                          {optionValue}
                        </Li>
                      )
                    })}
                  </Ul>
                </Column>
              </Row>
              <Row marginTop={6}>
                <Column span={12}>
                  <Link
                    data-testid="viewDetailsLinkFooter"
                    marginBottom={{ xs: 3 }}
                    href={destinationUrl}
                    fontSkin="body-standard"
                    onClick={() => {
                      trackNavigationClicks({
                        categoryId: merchandisingCategoryId,
                        pageZone: 'stockOptions',
                        destinationUrl,
                        tabIndex: tabIndex + 1,
                        stockOptionsCtaText: 'View full details',
                      })
                    }}
                  >
                    {viewDetailsCta}
                  </Link>
                </Column>
              </Row>
              {!isOutOfStock(mpvId, pageDataFromBFF) && (
                <Row>
                  <Column span={12}>
                    <Typography textColor="subtle">
                      <PriceDisplay data-testid="priceDisplay" mpvId={mpvId} />
                    </Typography>
                    <CouponCallout mpvId={mpvId} data-testid="couponCallout" />
                  </Column>
                </Row>
              )}

              <StockOptionCTA
                browseDesign={browseDesign}
                browseDesignCta={browseDesignCta}
                uploadDesignCta={uploadDesignCta}
                productName={item.name!}
                productId={item.coreProductId!}
                mpvId={mpvId}
                version={item.version!}
                quantities={item.quantities!}
                tabIndex={tabIndex}
                disableUploadFlow={item?.disableUploadFlow || false}
                pageDataFromBFF={pageDataFromBFF}
              />
            </Column>
          </Row>
        </TabContent>
      </>
    )
  )
}

export default StockOptionContent
